/**
 * EvaluationWizard Component
 * 
 * 스텝 기반 릴리즈 평가 폼 위자드
 * 애니메이션 전환과 함께 단계별 진행
 * 반응형: 모바일 / 태블릿 / 데스크탑
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { ReleaseInput, SuggestedTestCase } from '../types';
import { useLanguage } from '../i18n';
import { useAuth } from '../auth';
import { analytics } from '../analytics';
import { getTeam } from '../api';
import GitHubCommitReviewModal, { type GithubCommitSelectionSummary } from './GitHubCommitReviewModal';
import GitHubCommitReviewSection from './ReleaseForm/GitHubCommitReviewSection';

// 각 스텝 컴포넌트 import (기존 섹션 재사용 또는 새로 정의)
import BasicInfoSection from './ReleaseForm/BasicInfoSection';
import PlanningSection from './ReleaseForm/PlanningSection';
import RisksSection from './ReleaseForm/RisksSection';
import QAStatusSection from './ReleaseForm/QAStatusSection';
import SuggestedTestcasesSection from './ReleaseForm/SuggestedTestcasesSection';
import { useTestCaseAnalyzer, usePlanningAnalyzer } from './ReleaseForm/hooks';
import type { InputMode } from './ReleaseForm/types';

// 스텝 정의
type StepId = 'basic' | 'planning' | 'risks' | 'qa' | 'review';

interface Step {
  id: StepId;
  title: string;
  icon: string;
}

interface EvaluationWizardProps {
  onSubmit: (input: ReleaseInput) => Promise<void>;
  isLoading: boolean;
  prefillData?: ReleaseInput | null;
  isDemo?: boolean;
  onDemoSubmit?: () => void;
  onRequestOpenProfile?: () => void;
  onCancel: () => void;
}

export default function EvaluationWizard({
  onSubmit,
  isLoading,
  prefillData,
  isDemo = false,
  onDemoSubmit,
  onRequestOpenProfile,
  onCancel,
}: EvaluationWizardProps) {
  const { t, lang } = useLanguage();
  const { quota, user, isAuthenticated } = useAuth();

  // 스텝 정의
  const steps: Step[] = useMemo(() => [
    { id: 'basic', title: lang === 'ko' ? '기본정보/커밋' : 'Basic Info/Commits', icon: '📝' },
    { id: 'planning', title: lang === 'ko' ? '기획 의도' : 'Planning', icon: '🎯' },
    { id: 'risks', title: lang === 'ko' ? '릴리즈 리스크' : 'Release Risks', icon: '⚠️' },
    { id: 'qa', title: lang === 'ko' ? 'QA' : 'QA', icon: '✅' },
    { id: 'review', title: lang === 'ko' ? '최종확인' : 'Final Check', icon: '🧾' },
  ], [lang]);

  // 현재 스텝
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

  // 폼 데이터
  const [featureName, setFeatureName] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [planningIntent, setPlanningIntent] = useState('');
  const [risks, setRisks] = useState('');
  const [qaCore, setQaCore] = useState('');
  const [qaEdge, setQaEdge] = useState('');
  const [suggestedTestcases, setSuggestedTestcases] = useState<SuggestedTestCase[]>([]);
  const [isGeneratingTestcases, setIsGeneratingTestcases] = useState(false);
  
  // 팀/개인 저장 선택
  const [saveAsPersonal, setSaveAsPersonal] = useState(true); // 기본값: 개인
  const [teamInfo, setTeamInfo] = useState<{ team_id: number; team_name?: string } | null>(null);
  
  // Team 플랜 여부
  const isTeamPlan = quota?.plan === 'team';
  
  // 팀 정보 로드 (Team 플랜이면 시도, 팀이 없으면 null)
  useEffect(() => {
    if (!isTeamPlan || isDemo) {
      setTeamInfo(null);
      setSaveAsPersonal(true);
      return;
    }
    getTeam()
      .then((team) => {
        if (team) {
          setTeamInfo({ team_id: team.team.id, team_name: team.team.name });
          setSaveAsPersonal(false); // 팀이 있으면 기본값: 팀
        } else {
          setTeamInfo(null);
          setSaveAsPersonal(true);
        }
      })
      .catch(() => {
        setTeamInfo(null);
        setSaveAsPersonal(true);
      });
  }, [isTeamPlan, isDemo]);

  // GitHub 커밋 변경 분석 (평가 단위 상태)
  const githubConnected = !!(isAuthenticated && user?.github_connected);
  const [githubRepoFullName, setGithubRepoFullName] = useState('');
  const [showGithubCommitReview, setShowGithubCommitReview] = useState(false);
  const [githubSelectedCommits, setGithubSelectedCommits] = useState<GithubCommitSelectionSummary['commits']>([]);
  // 스텝형에서도 선택/적용은 유지 (현재는 테스트케이스 생성 입력에 반영하지 않지만 곧 확장 예정)
  const [_githubMerged, setGithubMerged] = useState<GithubCommitSelectionSummary['merged'] | null>(null);
  const [_githubHighlights, setGithubHighlights] = useState<GithubCommitSelectionSummary['highlights'] | null>(null);
  const LAST_GITHUB_CONTEXT_KEY = 'releasegate_last_github_context';

  // 1단계(basic) 내부 서브스텝: 커밋 요약(사이드이펙트/중요 코드) 확인 화면
  const [basicSubStep, setBasicSubStep] = useState<'form' | 'review'>('form');

  // 파일 업로드 상태
  const [planningInputMode, setPlanningInputMode] = useState<InputMode>('manual');
  const [qaInputMode, setQaInputMode] = useState<InputMode>('manual');
  const [didAutoSetQaImport, setDidAutoSetQaImport] = useState(false);
  const [planningFiles, setPlanningFiles] = useState<File[]>([]);
  const [testCaseFiles, setTestCaseFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  // 커스텀 훅 사용
  const {
    testCaseAnalysis,
    isAnalyzing: isTestCaseAnalyzing,
    analyzeTestCaseFiles,
    clearAnalysis: clearTestCaseAnalysis,
  } = useTestCaseAnalyzer();

  const {
    screenshotAnalyses,
    extractedPlanningItems,
    isAnalyzing: isPlanningAnalyzing,
    analyzePlanningFiles,
    clearAnalysis: clearPlanningAnalysis,
  } = usePlanningAnalyzer();

  const isAnalyzing = isTestCaseAnalyzing || isPlanningAnalyzing;

  // 4단계(QA) 기본값: '테스트 케이스 임포트' 탭이 선택된 상태로 시작
  useEffect(() => {
    const stepId = steps[currentStepIndex]?.id;
    if (stepId !== 'qa') return;
    if (isDemo) return;
    if (didAutoSetQaImport) return;
    if (testCaseFiles.length > 0) return;
    if (qaInputMode !== 'manual') return;
    setQaInputMode('file');
    setDidAutoSetQaImport(true);
  }, [currentStepIndex, didAutoSetQaImport, isDemo, qaInputMode, steps, testCaseFiles.length]);

  // prefillData 적용
  useEffect(() => {
    if (prefillData) {
      setFeatureName(prefillData.feature_name);
      setReleaseDate(prefillData.release_date);
      setPlanningIntent(prefillData.planning_intent.join('\n'));
      setRisks(prefillData.risks.join('\n'));
      const qaValues = Object.values(prefillData.qa_status);
      if (qaValues.length >= 1) setQaCore(qaValues[0]);
      if (qaValues.length >= 2) setQaEdge(qaValues[1]);
      if (prefillData.suggested_testcases) {
        setSuggestedTestcases(prefillData.suggested_testcases);
      }
    }
  }, [prefillData]);

  // 테스트 케이스 분석 결과 반영
  useEffect(() => {
    if (testCaseAnalysis) {
      setQaCore(testCaseAnalysis.coreStatus);
      setQaEdge(testCaseAnalysis.edgeStatus);
    }
  }, [testCaseAnalysis]);

  // 기획 의도 분석 결과 반영
  useEffect(() => {
    if (extractedPlanningItems.length > 0) {
      setPlanningIntent(extractedPlanningItems.join('\n'));
    }
  }, [extractedPlanningItems]);

  // 입력 제한 검증
  const inputValidation = useMemo(() => {
    const limits = quota?.limits ?? { max_test_cases: 10, max_planning_chars: 3000 };
    const planningLength = planningIntent.length;
    const testCaseCount = testCaseAnalysis?.total ?? 0;
    
    return {
      planningLength,
      planningLimit: limits.max_planning_chars,
      planningExceeded: planningLength > limits.max_planning_chars,
      testCaseCount,
      testCaseLimit: limits.max_test_cases,
      testCaseExceeded: testCaseCount > limits.max_test_cases,
      hasError: planningLength > limits.max_planning_chars || testCaseCount > limits.max_test_cases,
    };
  }, [planningIntent, testCaseAnalysis, quota]);

  // 스텝 유효성 검사
  const isStepValid = useCallback((stepId: StepId): boolean => {
    switch (stepId) {
      case 'basic':
        return !isDemo ? (featureName.trim() !== '' && releaseDate !== '') : true;
      case 'planning':
        return true; // 선택사항
      case 'risks':
        return true; // 선택사항
      case 'qa':
        return true; // 선택사항
      case 'review':
        return !inputValidation.hasError;
      default:
        return false;
    }
  }, [featureName, releaseDate, isDemo, inputValidation.hasError]);

  // 현재 스텝 유효성
  const currentStepValid = isStepValid(steps[currentStepIndex].id);

  // 전체 폼 유효성
  const isFormValid = steps.every(step => isStepValid(step.id)) && !inputValidation.hasError;

  // 에러 표시
  const showFileError = useCallback((message: string) => {
    setFileError(message);
    setTimeout(() => setFileError(null), 4000);
  }, []);

  const hasGithubReviewData = !!(_githubMerged && githubSelectedCommits.length > 0);

  // 다음 스텝
  const goNext = () => {
    if (currentStepIndex < steps.length - 1 && currentStepValid) {
      const stepId = steps[currentStepIndex]?.id;
      // 1단계(basic)에서는 먼저 "우려되는 사이드이펙트/중요 코드" 확인 화면을 보여줌 (스텝은 1단계 유지)
      if (stepId === 'basic' && !isDemo && basicSubStep === 'form' && hasGithubReviewData) {
        setBasicSubStep('review');
        return;
      }
      setDirection('forward');
      setCurrentStepIndex(prev => prev + 1);
      // basic을 벗어나면 서브스텝 초기화
      if (stepId === 'basic') setBasicSubStep('form');
    }
  };

  // 이전 스텝
  const goPrev = () => {
    const stepId = steps[currentStepIndex]?.id;
    if (stepId === 'basic' && basicSubStep === 'review') {
      setBasicSubStep('form');
      return;
    }
    if (currentStepIndex > 0) {
      setDirection('backward');
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  // 스텝 직접 이동
  const goToStep = (index: number) => {
    // 이전 스텝들이 모두 유효해야 이동 가능
    const canGo = steps.slice(0, index).every(step => isStepValid(step.id));
    if (canGo || index < currentStepIndex) {
      setDirection(index > currentStepIndex ? 'forward' : 'backward');
      setCurrentStepIndex(index);
    }
  };

  // 제출
  const handleSubmit = async () => {
    if (isDemo && onDemoSubmit) {
      onDemoSubmit();
      return;
    }

    // Result 리포트에서 표시할 GitHub 컨텍스트 저장 (ReleaseForm과 동일)
    try {
      if (githubSelectedCommits.length > 0 && _githubMerged && githubRepoFullName.trim()) {
        localStorage.setItem(
          LAST_GITHUB_CONTEXT_KEY,
          JSON.stringify({
            repo_full_name: githubRepoFullName.trim(),
            commits: githubSelectedCommits,
            merged: _githubMerged,
            highlights: _githubHighlights ?? null,
            captured_at: new Date().toISOString(),
          })
        );
      } else {
        localStorage.removeItem(LAST_GITHUB_CONTEXT_KEY);
      }
    } catch {
      // ignore
    }

    const coreKey = lang === 'ko' ? '핵심 시나리오' : 'Core Scenario';
    const edgeKey = lang === 'ko' ? '엣지 케이스' : 'Edge Cases';

    const input: ReleaseInput = {
      feature_name: featureName,
      release_date: releaseDate,
      planning_intent: planningIntent.split('\n').filter(line => line.trim()),
      risks: risks.split('\n').filter(line => line.trim()),
      qa_status: {
        [coreKey]: qaCore || t.qaValue.notEntered,
        [edgeKey]: qaEdge || t.qaValue.notEntered,
      },
      lang: lang,
      suggested_testcases: suggestedTestcases.length > 0 ? suggestedTestcases : undefined,
      // 팀 소속 사용자의 개인 저장 선택
      save_as_personal: teamInfo ? saveAsPersonal : undefined,
    };

    // GitHub 컨텍스트(선택): 히스토리/리포트/PDF에서 동일하게 재생성할 수 있도록 서버에도 함께 전송
    if (githubSelectedCommits.length > 0 && _githubMerged && githubRepoFullName.trim()) {
      input.github_context = {
        repo_full_name: githubRepoFullName.trim(),
        commits: githubSelectedCommits,
        merged: _githubMerged,
        highlights: _githubHighlights ?? null,
        captured_at: new Date().toISOString(),
      };
    } else {
      input.github_context = null;
    }

    // 테스트케이스 파일이 있으면 base64로 인코딩하여 추가 (ReleaseForm과 동일)
    if (testCaseFiles.length > 0) {
      const encodedTestCaseFiles: { filename: string; content: string; content_type?: string }[] = [];
      for (const file of testCaseFiles) {
        try {
          const fileContent = await file.arrayBuffer();
          const base64Content = btoa(
            new Uint8Array(fileContent).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          encodedTestCaseFiles.push({
            filename: file.name,
            content: base64Content,
            content_type: file.type || undefined,
          });
        } catch {
          // 개별 파일 인코딩 실패해도 계속 진행
        }
      }
      if (encodedTestCaseFiles.length > 0) {
        input.testcase_files = encodedTestCaseFiles;
        // 기존 호환성: 첫 번째 파일은 testcase_file에도 저장
        input.testcase_file = encodedTestCaseFiles[0];
      }
    }

    // 기획 파일이 있으면 base64로 인코딩하여 추가 (ReleaseForm과 동일)
    if (planningFiles.length > 0) {
      const encodedPlanningFiles: { filename: string; content: string; content_type?: string }[] = [];
      for (const file of planningFiles) {
        try {
          const fileContent = await file.arrayBuffer();
          const base64Content = btoa(
            new Uint8Array(fileContent).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          encodedPlanningFiles.push({
            filename: file.name,
            content: base64Content,
            content_type: file.type || undefined,
          });
        } catch {
          // 개별 파일 인코딩 실패해도 계속 진행
        }
      }
      if (encodedPlanningFiles.length > 0) {
        input.planning_files = encodedPlanningFiles;
      }
    }

    analytics.evaluateSubmit(testCaseFiles.length > 0, planningFiles.length > 0);
    await onSubmit(input);
  };

  // 현재 스텝 컴포넌트 렌더링
  const renderCurrentStep = () => {
    const currentStep = steps[currentStepIndex];

    switch (currentStep.id) {
      case 'basic':
        return basicSubStep === 'review' ? (
          <div className="space-y-6">
            <div className="neo-card-static bg-terminal-bg border border-terminal-border rounded-xl p-4">
              <div className="text-terminal-muted text-xs mb-2 uppercase tracking-wide">
                # {lang === 'ko' ? '1단계: 기본정보/커밋' : 'Step 1: Basic Info/Commits'}
              </div>
              <div className="text-terminal-text text-sm">
                {lang === 'ko'
                  ? '2단계로 넘어가기 전에, 선택한 커밋 기준으로 중점 점검 영역/우려되는 사이드이펙트/중요 코드·함수를 확인하세요.'
                  : 'Before moving on, review focus areas, side effects, and key code/functions from the selected commits.'}
              </div>
            </div>

            <GitHubCommitReviewSection
              isConnected={githubConnected}
              githubUsername={user?.github_username}
              repoFullName={githubRepoFullName}
              setRepoFullName={setGithubRepoFullName}
              onOpenModal={() => setShowGithubCommitReview(true)}
              onConnect={() => onRequestOpenProfile?.()}
              selectedCommits={githubSelectedCommits}
              merged={_githubMerged}
              highlights={_githubHighlights}
              hideRepoInput
              onClearSelection={() => {
                setGithubSelectedCommits([]);
                setGithubMerged(null);
                setGithubHighlights(null);
                setBasicSubStep('form');
              }}
              isDemo={isDemo}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <BasicInfoSection
              featureName={featureName}
              setFeatureName={setFeatureName}
              releaseDate={releaseDate}
              setReleaseDate={setReleaseDate}
              isDemo={isDemo}
              isReEvaluate={!!prefillData?.is_re_evaluate}
              isTeamPlan={isTeamPlan}
              teamInfo={teamInfo}
              saveAsPersonal={saveAsPersonal}
              setSaveAsPersonal={setSaveAsPersonal}
            />

            <GitHubCommitReviewSection
              isConnected={githubConnected}
              githubUsername={user?.github_username}
              repoFullName={githubRepoFullName}
              setRepoFullName={setGithubRepoFullName}
              onOpenModal={() => setShowGithubCommitReview(true)}
              onConnect={() => onRequestOpenProfile?.()}
              selectedCommits={githubSelectedCommits}
              merged={_githubMerged}
              highlights={_githubHighlights}
              hideMergedSummary
              onClearSelection={() => {
                setGithubSelectedCommits([]);
                setGithubMerged(null);
                setGithubHighlights(null);
              }}
              isDemo={isDemo}
            />
          </div>
        );

      case 'planning':
        return (
          <PlanningSection
            planningIntent={planningIntent}
            setPlanningIntent={setPlanningIntent}
            planningInputMode={planningInputMode}
            setPlanningInputMode={setPlanningInputMode}
            planningFiles={planningFiles}
            setPlanningFiles={setPlanningFiles}
            screenshotAnalyses={screenshotAnalyses}
            extractedPlanningItems={extractedPlanningItems}
            isAnalyzing={isPlanningAnalyzing}
            analyzePlanningFiles={analyzePlanningFiles}
            clearPlanningAnalysis={clearPlanningAnalysis}
            showFileError={showFileError}
            charCount={inputValidation.planningLength}
            charLimit={inputValidation.planningLimit}
            isDemo={isDemo}
            featureName={featureName}
          />
        );

      case 'risks':
        return (
          <RisksSection
            risks={risks}
            setRisks={setRisks}
            isDemo={isDemo}
          />
        );

      case 'qa':
        return (
          <div className="space-y-6">
            <QAStatusSection
              qaCore={qaCore}
              setQaCore={setQaCore}
              qaEdge={qaEdge}
              setQaEdge={setQaEdge}
              qaInputMode={qaInputMode}
              setQaInputMode={setQaInputMode}
              testCaseFiles={testCaseFiles}
              setTestCaseFiles={setTestCaseFiles}
              testCaseAnalysis={testCaseAnalysis}
              isAnalyzing={isTestCaseAnalyzing}
              analyzeTestCaseFiles={analyzeTestCaseFiles}
              clearTestCaseAnalysis={clearTestCaseAnalysis}
              showFileError={showFileError}
              testCaseLimit={inputValidation.testCaseLimit}
              testCaseFileLimit={quota?.limits?.max_testcase_files ?? 1}
              isDemo={isDemo}
              featureName={featureName}
            />
            <SuggestedTestcasesSection
              featureName={featureName}
              planningIntent={planningIntent}
              risks={risks}
              suggestedTestcases={suggestedTestcases}
              setSuggestedTestcases={setSuggestedTestcases}
              isDemo={isDemo}
              qaCore={qaCore}
              qaEdge={qaEdge}
              githubSummary={
                githubSelectedCommits.length > 0 && _githubMerged && githubRepoFullName.trim()
                  ? { repoFullName: githubRepoFullName.trim(), commits: githubSelectedCommits, merged: _githubMerged }
                  : undefined
              }
              onGeneratingChange={setIsGeneratingTestcases}
            />
          </div>
        );

      case 'review': {
        const hasGithub = githubSelectedCommits.length > 0 && !!_githubMerged && !!githubRepoFullName.trim();
        const planningLines = planningIntent.split('\n').filter((l) => l.trim()).length;
        const riskLines = risks.split('\n').filter((l) => l.trim()).length;
        const tcCount = suggestedTestcases.length;
        const importedTcCount = testCaseAnalysis?.total ?? 0;

        return (
          <div className="space-y-4">
            <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-5 border border-terminal-border rounded-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-black text-terminal-text">
                    {lang === 'ko' ? '리포트' : 'Report'}
                  </div>
                  <div className="text-xs text-terminal-muted mt-1">
                    {lang === 'ko'
                      ? '입력한 내용이 맞는지 확인하고 “분석 시작”을 눌러주세요.'
                      : 'Confirm inputs and start analysis.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => goToStep(0)}
                  className="px-3 py-2 bg-terminal-input border border-terminal-border rounded-lg text-terminal-muted text-xs hover:text-terminal-text hover:border-terminal-muted transition-colors"
                >
                  {lang === 'ko' ? '처음으로' : 'Back to start'}
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
                <div className="p-3 rounded border border-terminal-border bg-terminal-dim/10">
                  <div className="text-xs text-terminal-muted">{lang === 'ko' ? '릴리즈' : 'Release'}</div>
                  <div className="text-terminal-text font-semibold mt-1">{featureName || (lang === 'ko' ? '(미입력)' : '(empty)')}</div>
                  <div className="text-xs text-terminal-muted mt-1">{releaseDate || (lang === 'ko' ? '(미입력)' : '(empty)')}</div>
                </div>
                <div className="p-3 rounded border border-terminal-border bg-terminal-dim/10">
                  <div className="text-xs text-terminal-muted">{lang === 'ko' ? 'GitHub/커밋' : 'GitHub/Commits'}</div>
                  <div className="text-terminal-text font-semibold mt-1">
                    {hasGithub ? githubRepoFullName.trim() : (lang === 'ko' ? '선택 없음' : 'Not selected')}
                  </div>
                  <div className="text-xs text-terminal-muted mt-1">
                    {hasGithub
                      ? (lang === 'ko' ? `${githubSelectedCommits.length}개 커밋` : `${githubSelectedCommits.length} commits`)
                      : (lang === 'ko' ? '선택사항' : 'Optional')}
                  </div>
                </div>
                <div className="p-3 rounded border border-terminal-border bg-terminal-dim/10">
                  <div className="text-xs text-terminal-muted">{lang === 'ko' ? '기획/리스크' : 'Plan/Risk'}</div>
                  <div className="text-xs text-terminal-text mt-1">
                    {lang === 'ko'
                      ? `기획 ${planningLines}줄 · 리스크 ${riskLines}줄`
                      : `Planning ${planningLines} lines · Risks ${riskLines} lines`}
                  </div>
                </div>
                <div className="p-3 rounded border border-terminal-border bg-terminal-dim/10">
                  <div className="text-xs text-terminal-muted">{lang === 'ko' ? '테스트케이스' : 'Test Cases'}</div>
                  <div className="text-xs text-terminal-text mt-1">
                    {lang === 'ko'
                      ? `임포트 분석 ${importedTcCount}개 · 현재 목록 ${tcCount}개`
                      : `Imported ${importedTcCount} · Current ${tcCount}`}
                  </div>
                </div>
              </div>

              {inputValidation.hasError && (
                <div className="mt-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded text-sm text-status-danger">
                  {lang === 'ko' ? '입력 제한을 초과했습니다. 이전 단계로 돌아가 줄여주세요.' : 'Input limits exceeded. Go back and reduce.'}
                </div>
              )}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className="space-y-6"
      data-testid="evaluation-wizard"
    >
      {/* 분석/평가 중 전체 화면 디밍 */}
      {(isAnalyzing || isLoading || isGeneratingTestcases) && (
        <div className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 px-4">
            <div className="w-12 h-12 border-4 border-status-warning/30 border-t-status-warning rounded-full animate-spin"></div>
            <div className="text-terminal-text text-base font-mono">
              {isLoading ? t.form.evaluating : t.form.analyzing}
              <span className="cursor-blink"></span>
            </div>
            <div className="text-terminal-muted text-sm text-center">
              {lang === 'ko'
                ? (isLoading ? 'AI가 릴리즈 준비 상태를 평가 중입니다...' : '파일/내용을 분석 중입니다...')
                : (isLoading ? 'AI is evaluating release readiness...' : 'Analyzing content/files...')}
            </div>
          </div>
        </div>
      )}
      {/* 프로그레스 인디케이터 */}
      <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4" data-testid="wizard-progress">
        {/* 데스크탑: 가로 스테퍼 */}
        <div className="hidden md:block">
          {/* ㅁ-ㅁ-ㅁ-ㅁ-ㅁ 형태: 단계는 박스, 사이는 커넥터 */}
          <div
            className="grid items-center gap-2"
            style={{
              gridTemplateColumns:
                'minmax(0,1fr) 28px minmax(0,1fr) 28px minmax(0,1fr) 28px minmax(0,1fr) 28px minmax(0,1fr)',
            }}
            data-testid="wizard-progress-desktop"
          >
            {steps.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const isAccessible = index <= currentStepIndex || steps.slice(0, index).every(s => isStepValid(s.id));

              return (
                <div key={step.id} className="contents">
                  <button
                    onClick={() => isAccessible && goToStep(index)}
                    disabled={!isAccessible}
                    className={`w-full min-w-0 flex items-center justify-center px-3 py-2 rounded-lg border transition-all whitespace-nowrap ${
                      isCurrent
                        ? 'bg-[var(--neo-mint)] text-black font-bold border-[var(--neo-mint)]'
                        : isCompleted
                          ? 'bg-status-go/15 text-status-go border-status-go/40'
                          : isAccessible
                            ? 'bg-terminal-input text-terminal-muted hover:bg-terminal-dim border-terminal-border'
                            : 'bg-terminal-input/50 text-terminal-dim cursor-not-allowed border-terminal-border'
                    }`}
                    data-testid={`wizard-step-${step.id}`}
                  >
                    <span className="text-xs whitespace-nowrap">{index + 1}. {step.title}</span>
                  </button>
                  {index < steps.length - 1 && (
                    <div className="flex items-center justify-center" aria-hidden="true">
                      <div className={`h-0.5 w-full ${isCompleted ? 'bg-status-go' : 'bg-terminal-border'}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 모바일/태블릿: 압축형 스테퍼 */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-terminal-text">
              {steps[currentStepIndex].icon} {steps[currentStepIndex].title}
            </span>
            <span className="text-xs text-terminal-muted">
              {currentStepIndex + 1} / {steps.length}
            </span>
          </div>
          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  index < currentStepIndex
                    ? 'bg-status-go'
                    : index === currentStepIndex
                      ? 'bg-[var(--neo-mint)]'
                      : 'bg-terminal-border'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 스텝 컨텐츠 (애니메이션) */}
      <div 
        className={`transition-all duration-300 ease-out ${
          direction === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft'
        }`}
        key={currentStepIndex}
        data-testid="wizard-content"
      >
        {renderCurrentStep()}
      </div>

      {/* 에러 토스트 */}
      {fileError && (
        <div className="fixed top-4 right-4 z-50 p-4 bg-status-danger text-white rounded-lg shadow-lg animate-slide-in" data-testid="wizard-error-toast">
          {fileError}
        </div>
      )}

      {/* 입력 제한 경고 */}
      {inputValidation.hasError && (
        <div className="p-3 bg-status-danger/10 border border-status-danger/30 rounded text-sm text-status-danger" data-testid="wizard-validation-error">
          <p className="font-medium mb-1">
            {lang === 'ko' ? '⚠️ 입력 제한 초과' : '⚠️ Input Limit Exceeded'}
          </p>
          <ul className="list-disc list-inside text-xs">
            {inputValidation.planningExceeded && (
              <li>
                {lang === 'ko'
                  ? `Planning: ${inputValidation.planningLength.toLocaleString()}자 / ${inputValidation.planningLimit.toLocaleString()}자`
                  : `Planning: ${inputValidation.planningLength.toLocaleString()} / ${inputValidation.planningLimit.toLocaleString()} chars`}
              </li>
            )}
            {inputValidation.testCaseExceeded && (
              <li>
                {lang === 'ko'
                  ? `테스트케이스: ${inputValidation.testCaseCount}개 / ${inputValidation.testCaseLimit}개`
                  : `Test cases: ${inputValidation.testCaseCount} / ${inputValidation.testCaseLimit}`}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* 네비게이션 (컨텐츠 끝 -> 버튼 -> 푸터 순서) */}
      <div className="mt-4" data-testid="wizard-navigation-section">
        <div className="flex gap-3 items-stretch" data-testid="wizard-navigation">
            <button
              onClick={goPrev}
              disabled={currentStepIndex === 0}
              className={`neo-btn px-4 py-3 text-sm bg-white dark:bg-terminal-input text-black dark:text-terminal-muted ${
                currentStepIndex === 0 ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              data-testid="wizard-prev-btn"
            >
              {lang === 'ko' ? '이전' : 'Back'}
            </button>

            {currentStepIndex < steps.length - 1 ? (
              <button
                onClick={goNext}
                disabled={!currentStepValid || isAnalyzing}
                className={`flex-1 py-3 neo-btn text-sm ${
                  currentStepValid && !isAnalyzing
                    ? 'bg-[var(--neo-mint)] text-black'
                    : 'bg-terminal-input text-terminal-dim cursor-not-allowed'
                }`}
                data-testid="wizard-next-btn"
              >
                {isAnalyzing ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    {t.form.analyzing}
                  </span>
                ) : (
                  lang === 'ko' ? '다음' : 'Next'
                )}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || isLoading || isAnalyzing || isGeneratingTestcases}
                className={`flex-1 py-3 neo-btn text-sm ${
                  isFormValid && !isLoading && !isAnalyzing && !isGeneratingTestcases
                    ? isDemo
                      ? 'bg-[var(--neo-mint)]/20 border-[var(--neo-mint)] text-[var(--neo-mint)]'
                      : 'bg-status-go/10 border-status-go text-status-go'
                    : 'bg-terminal-input text-terminal-dim cursor-not-allowed'
                }`}
                data-testid="wizard-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    {t.form.evaluating}
                  </span>
                ) : isDemo ? (
                  lang === 'ko' ? '> 데모 결과 보기' : '> View Demo Result'
                ) : (
                  t.form.runEvaluate
                )}
              </button>
            )}

            <button
              onClick={onCancel}
              className="neo-btn px-4 py-3 text-sm bg-white dark:bg-terminal-input text-black dark:text-terminal-muted"
              data-testid="wizard-cancel-btn"
            >
              {lang === 'ko' ? '취소' : 'Cancel'}
            </button>
        </div>
      </div>

      {/* 데모 모드 안내 */}
      {isDemo && (
        <div className="flex items-center justify-center gap-2 text-xs text-terminal-muted" data-testid="wizard-demo-hint">
          <span className="w-2 h-2 bg-[var(--neo-mint)] rounded-full animate-pulse"></span>
          {lang === 'ko'
            ? '로그인하면 직접 입력하여 AI 평가를 받을 수 있습니다'
            : 'Login to input your own data and get AI evaluation'}
        </div>
      )}

      <GitHubCommitReviewModal
        isOpen={showGithubCommitReview}
        onClose={() => setShowGithubCommitReview(false)}
        repoFullName={githubRepoFullName}
        setRepoFullName={setGithubRepoFullName}
        initialSelectedShas={githubSelectedCommits.map((c) => c.sha)}
        onApply={(summary) => {
          setGithubRepoFullName(summary.repo_full_name);
          setGithubSelectedCommits(summary.commits);
          setGithubMerged(summary.merged);
          setGithubHighlights(summary.highlights ?? null);
        }}
      />

      {/* 커스텀 애니메이션 스타일 */}
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.3s ease-out;
        }

        /* 위자드에서 2/3단계 입력 영역을 더 크게(남는 공간 활용) */
        [data-testid="evaluation-wizard"] textarea[data-testid="input-planning-intent"] {
          min-height: clamp(260px, 45vh, 560px);
        }
        [data-testid="evaluation-wizard"] textarea[data-testid="input-risks"] {
          min-height: clamp(220px, 40vh, 480px);
        }
      `}</style>
    </div>
  );
}

